import type { Graphics } from 'pixi.js';

const HAIR_COLOR = 0x4a3520;
const SKIN_COLOR = 0xf5cfa0;
const SKIN_OUTLINE = 0xd4a860;
const PANTS_COLOR = 0x334466;
const HEAD_R = 3;

/** Draw a person top-down. If animPhase is provided, legs/arms animate. */
export function drawPerson(
  g: Graphics,
  x: number,
  y: number,
  shirtColor: number,
  hairStyle: number,
  animPhase: number = 0,
  scale: number = 1,
  options?: { hairColor?: number; pantsColor?: number },
): void {
  const s = scale;
  const hairClr = options?.hairColor ?? HAIR_COLOR;
  const pantsClr = options?.pantsColor ?? PANTS_COLOR;
  const armSwing = Math.sin(animPhase) * 3;
  const bounce = Math.abs(Math.sin(animPhase)) * 1.2;
  const legL = Math.sin(animPhase) * 2;
  const legR = Math.sin(animPhase + Math.PI) * 2;

  // Long hair behind body (drawn first = behind everything)
  if (hairStyle === 2) {
    g.moveTo(x - 5 * s, y + 0 * s);
    g.lineTo(x - 7 * s, y + 7 * s);
    g.quadraticCurveTo(x, y + 9 * s, x + 7 * s, y + 7 * s);
    g.lineTo(x + 5 * s, y + 0 * s);
    g.closePath();
    g.fill({ color: hairClr });
  }

  // Legs
  g.moveTo(x - 2 * s, y + 3 * s);
  g.lineTo(x - 3 * s, y + (7 + legL) * s);
  g.stroke({ color: pantsClr, width: 2 * s, cap: 'round' });
  g.moveTo(x + 2 * s, y + 3 * s);
  g.lineTo(x + 3 * s, y + (7 + legR) * s);
  g.stroke({ color: pantsClr, width: 2 * s, cap: 'round' });

  // Torso
  g.ellipse(x, y - bounce * s, 3 * s, 4 * s);
  g.fill({ color: shirtColor });

  // Arms
  g.moveTo(x + 3 * s, y + (-1 - bounce) * s);
  g.lineTo(x + (6 + armSwing) * s, y + (2 - bounce) * s);
  g.stroke({ color: SKIN_COLOR, width: 2 * s, cap: 'round' });
  g.moveTo(x - 3 * s, y + (-1 - bounce) * s);
  g.lineTo(x + (-6 - armSwing) * s, y + (2 - bounce) * s);
  g.stroke({ color: SKIN_COLOR, width: 2 * s, cap: 'round' });

  // Head
  const headY = y + (-5 - bounce) * s;
  g.circle(x, headY, HEAD_R * s);
  g.fill({ color: SKIN_COLOR });
  g.stroke({ color: SKIN_OUTLINE, width: 0.7 * s });

  // Hair cap (short and long both get it)
  if (hairStyle >= 1) {
    g.moveTo(x - HEAD_R * s, headY);
    g.lineTo(x + HEAD_R * s, headY);
    g.arc(x, headY, HEAD_R * s, 0, -Math.PI, true);
    g.closePath();
    g.fill({ color: hairClr });
  }
}

/** Draw a static person on the boat (no animation, slightly different pose). */
export function drawBoatPerson(
  g: Graphics,
  shirtColor: number,
  hairStyle: number,
  hairColor: number = HAIR_COLOR,
): void {
  // Long hair behind body
  if (hairStyle === 2) {
    g.moveTo(-5, -3);
    g.lineTo(-7, 4);
    g.quadraticCurveTo(0, 6, 7, 4);
    g.lineTo(5, -3);
    g.closePath();
    g.fill({ color: hairColor });
  }

  // Torso
  g.ellipse(0, 0, 3, 4);
  g.fill({ color: shirtColor });

  // Legs
  g.circle(-2, 5, 1.5);
  g.circle(2, 5, 1.5);
  g.fill({ color: PANTS_COLOR });

  // Arms
  g.moveTo(3, -1);
  g.lineTo(7, -3);
  g.stroke({ color: SKIN_COLOR, width: 2, cap: 'round' });
  g.moveTo(-3, 0);
  g.lineTo(-5, 4);
  g.stroke({ color: SKIN_COLOR, width: 2, cap: 'round' });

  // Head
  g.circle(0, -4, HEAD_R);
  g.fill({ color: SKIN_COLOR });
  g.stroke({ color: SKIN_OUTLINE, width: 0.7 });

  // Hair cap
  if (hairStyle >= 1) {
    g.moveTo(-HEAD_R, -4);
    g.lineTo(HEAD_R, -4);
    g.arc(0, -4, HEAD_R, 0, -Math.PI, true);
    g.closePath();
    g.fill({ color: hairColor });
  }
}
